import { createContext, useContext, useEffect, useState } from "react";
import { setSession } from "src/api/api";
import jwtDecode from "jwt-decode";
import { signIn } from "src/api/publicRoutes";
import { LocalizationContext } from "src/App";
import { useNavigate } from "react-router";
import { SignInOutcome } from "src/interfaces/admin";

const isValidToken = (accessToken: string) => {
  const decodedToken = jwtDecode(accessToken) as { exp: number };
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

export interface AuthContextInterface {
  role: string;
  uid: string;
  username: string;
  first_name: string;
  last_name: string;
  timezone: string;
}

const defaultState: AuthContextInterface = {
  role: "",
  uid: "",
  username: "",
  first_name: "",
  last_name: "",
  timezone: "",
};

export const AuthContext = createContext({
  ...defaultState,
  login: (user: string, password: string, browser: string) =>
    Promise.resolve<SignInOutcome>({ kind: "error", message: "" }),
  refreshSession: (token: string) => {},
  logout: () => {},
});


let logoutFunction: () => void;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const { setLanguage } = useContext(LocalizationContext);

  const logout = () => {
    setState(defaultState);
    setSession(null);
    navigate("/signin");
  };

  logoutFunction = logout;

  const applyToken = (token: string) => {
    const claims = jwtDecode(token) as {
      role: string;
      uid: string;
      username: string;
      first_name: string;
      last_name: string;
      lang: string;
      timezone: string;
    };
    if (isValidToken(token)) {
      setState(claims);
    }
    setLanguage(claims.lang);
  };

  const refreshSession = (token: string) => {
    setSession(token);
    applyToken(token);
  };

  const login = async (user: string, password: string, browser: string): Promise<SignInOutcome> => {
    const outcome = await signIn(user, password, browser);
    if (outcome.kind === "session") {
      applyToken(outcome.token);
    }
    return outcome;
  };

  const token = window.localStorage.getItem("accessToken");
  const jwt: {
    role: string;
    uid: string;
    username: string;
    first_name: string;
    last_name: string;
    lang: string;
    timezone: string;
  } | undefined = !token ? undefined : jwtDecode(token);

  useEffect(() => {
    const fetchToken = (async () => {
      
      if (!token) {return;}
      
      if (token && isValidToken(token)) {
        setSession(token);
      }

      setLanguage(jwt.lang);
    });
    fetchToken();
  }, [setLanguage, jwt, token]);

  const [state, setState] = useState<AuthContextInterface>(jwt ? jwt : defaultState);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        refreshSession,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const signout =  () => {
  return logoutFunction();
}
